import { useMemo, type CSSProperties, type ReactNode } from 'react'
import { Spinner, Text, VisuallyHidden } from '@n26/nxd'
import type { CardStub } from '@/features/dashboard/api/schemas'
import { formatLike, toFigure } from '@/features/dashboard/format/figure'
import { useDeepDiveCard } from '@/features/dashboard/filters/useDeepDiveCard'
import { DataTable } from '@/features/dashboard/charts/DataTable'
import { DeepDiveChart } from '@/features/dashboard/charts/DeepDiveChart'
import { chartToTable } from '@/features/dashboard/charts/lib/chartTable'
import {
  deepDiveKind,
  isTableCard,
} from '@/features/dashboard/charts/lib/deepDiveKind'
import {
  maskBudgets,
  periodWindow,
} from '@/features/dashboard/charts/lib/series'
import { CardFigure } from '@/features/dashboard/tiles/CardFigure'
import { EmptyCardMessage } from '@/features/dashboard/tiles/EmptyCardMessage'
import { MarketFilterNote } from '@/features/dashboard/tiles/MarketFilterNote'
import { PendingBody } from '@/features/dashboard/tiles/PendingBody'
import { cx } from '@/shared/lib/classNames'
import { InfoTip } from '@/shared/ui/InfoTip'
import styles from './DeepDivePage.module.css'

export const Loading = () => (
  <div role='status'>
    <Spinner />
    <VisuallyHidden>Loading</VisuallyHidden>
  </div>
)

export type Heading = 'h2' | 'h3'

export type DeepDiveCardProps = {
  stub: CardStub
  deepDiveId: string
  /** Below the page's and the subsection's headings. */
  heading: Heading
  /** Shows a chart card as a table (the page's 'View as table'). */
  asTable: boolean
  /** Lays the card out in its group's grid. */
  className?: string
  style?: CSSProperties | undefined
}

const SIZE_CLASSES = {
  'single-metric': styles.singleMetric,
  wide: styles.wide,
  'half-wide': styles.halfWide,
  half: styles.half,
}

const sizeClass = (stub: CardStub) =>
  stub.layout?.size ? SIZE_CLASSES[stub.layout.size] : undefined

const CardTitle = ({
  stub,
  heading: Level,
}: Pick<DeepDiveCardProps, 'stub' | 'heading'>) => (
  <div className={styles.titleRow}>
    <Level className={styles.cardTitle}>{stub.name}</Level>
    {stub.description && (
      <InfoTip name={stub.name} description={stub.description} />
    )}
  </div>
)

/** A Figma tile with no data yet: drawn without a request. */
const PendingCard = ({
  stub,
  heading,
  className,
  style,
}: Omit<DeepDiveCardProps, 'deepDiveId' | 'asTable'>) => (
  <section
    className={cx(styles.chart, className, sizeClass(stub))}
    style={style}
    aria-label={stub.name}
  >
    <CardTitle stub={stub} heading={heading} />
    <PendingBody />
  </section>
)

const LiveDeepDiveCard = ({
  stub,
  deepDiveId,
  heading,
  asTable,
  className,
  style,
}: DeepDiveCardProps) => {
  const {
    data: card,
    isPending,
    isError,
    clearMarkets,
    timeframe,
    months,
  } = useDeepDiveCard(stub, deepDiveId)
  // A card taking the timeframe is cut to the page's range, as at home.
  const windowed = useMemo(
    () =>
      card?.kind === 'chart' && timeframe !== undefined
        ? periodWindow(maskBudgets(card), timeframe, months)
        : null,
    [card, timeframe, months]
  )
  const placement = cx(className, sizeClass(stub))

  if (isPending) {
    return (
      <section className={cx(styles.chart, placement)} style={style}>
        <Loading />
      </section>
    )
  }
  if (isError) {
    return (
      <div className={cx(styles.chart, placement)} style={style} role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load this card.</Text>
      </div>
    )
  }
  if (card.kind === 'kpi') {
    const figure = toFigure(card)
    return (
      <section
        className={cx(styles.kpi, placement)}
        style={style}
        aria-label={card.name}
      >
        <CardTitle stub={stub} heading={heading} />
        {figure && (
          <CardFigure
            figure={figure}
            size='expanded'
            className={styles.kpiValues}
          />
        )}
        {figure && figure.previous !== null && (
          <p className={styles.previous}>
            Previous: {formatLike(figure, figure.previous)}
          </p>
        )}
      </section>
    )
  }

  let body: ReactNode
  let isMap = false
  if (card.kind === 'empty') {
    body = <EmptyCardMessage reason={card.reason} onClear={clearMarkets} />
  } else if (isTableCard(card, stub.chart)) {
    body =
      card.table && card.table.rows.length > 0 ? (
        <DataTable table={card.table} title={card.name} unit={card.unit} />
      ) : (
        <EmptyCardMessage reason='no-rows' onClear={clearMarkets} />
      )
  } else {
    const shown = windowed ?? card
    // 'View as table': the chart's own figures, in the table's chrome.
    const viewed = asTable ? chartToTable(shown, stub.chart) : null
    isMap = !viewed && deepDiveKind(card, stub.chart) === 'map'
    body = viewed ? (
      <DataTable table={viewed} title={card.name} unit={card.unit} />
    ) : (
      <DeepDiveChart card={shown} chart={stub.chart} timeframe={timeframe} />
    )
  }

  return (
    <section
      className={cx(styles.chart, isMap && styles.map, placement)}
      style={style}
      aria-label={card.name}
    >
      <CardTitle stub={stub} heading={heading} />
      {card.kind === 'chart' && card.marketFiltered === false && (
        <MarketFilterNote />
      )}
      {body}
    </section>
  )
}

export const DeepDiveCard = (props: DeepDiveCardProps) =>
  props.stub.pending ? (
    <PendingCard {...props} />
  ) : (
    <LiveDeepDiveCard {...props} />
  )
